import React, { useMemo, useRef, useState } from "react";
import {
  IconCall,
  IconEmail,
  IconLocationPin,
  IconMessenger,
} from "../../assets/icons";
import { ContactService } from "../../services/contactService";
import { toast } from "react-toastify";

export default function ContactPage() {
  // ====== STATE ======
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "Hỗ trợ tư vấn",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const formRef = useRef(null);
  const phoneRef = useRef(null);

  // ====== VALIDATION ======
  const schema = useMemo(
    () => ({
      name: (v) => (v?.trim() ? "" : "Vui lòng nhập họ tên"),
      phone: (v) =>
        /^\+?\d{9,15}$/.test(v?.replace(/\s/g, ""))
          ? ""
          : "Số điện thoại không hợp lệ",
      email: (v) => (!v || /.+@.+\..+/.test(v) ? "" : "Email không hợp lệ"),
      message: (v) => (v?.trim() ? "" : "Vui lòng nhập nội dung"),
    }),
    []
  );

  const validateAll = () => {
    const e = {
      name: schema.name(form.name),
      phone: schema.phone(form.phone),
      email: schema.email(form.email),
      message: schema.message(form.message),
    };
    Object.keys(e).forEach((k) => !e[k] && delete e[k]);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ====== EVENTS ======
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "idle", msg: "" });
    const ok = validateAll();
    if (!ok) return;

    // Blur input để đóng bàn phím trên mobile
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setSubmitting(true);
    try {
      const res = await ContactService.sendMail(JSON.stringify(form));
      if (!res || !res.Data) {
        throw new Error("Request failed");
      }
      toast.success("Gửi yêu cầu thành công!");
      setStatus({
        type: "success",
        msg: "Gửi yêu cầu thành công! Chúng tôi sẽ liên hệ sớm.",
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        subject: "Hỗ trợ tư vấn",
        message: "",
      });
      formRef.current?.reset?.();
    } catch (err) {
      toast.error("Lỗi gửi yêu cầu!");
      setStatus({
        type: "error",
        msg: "Không thể gửi yêu cầu. Vui lòng thử lại sau.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  console.log("status", status);

  // ====== HELPERS ======
  const InfoItem = ({ icon, label, children, href }) => (
    <a
      href={href || undefined}
      target={
        href ? (href.startsWith("http") ? "_blank" : undefined) : undefined
      }
      rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
      className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition"
    >
      <span className="mt-0.5">{icon}</span>
      <span>
        <span className="block text-sm text-gray-500">{label}</span>
        <span className="block text-base lg:text-lg font-medium group-hover:text-black">
          {children}
        </span>
      </span>
    </a>
  );

  return (
    <div className="w-full min-h-[70vh] bg-white">
      {/* Hero / Heading */}
      <section className="px-4 lg:px-8 py-8 lg:py-12 border-b">
        <h1 className="text-2xl lg:text-4xl font-bold">Liên hệ</h1>
        <p className="text-gray-600 mt-2 lg:text-lg">
          Bạn cần tư vấn sản phẩm, báo giá, hay hỗ trợ kỹ thuật? Hãy để lại
          thông tin - Ly iPhone sẽ phản hồi sớm nhất.
        </p>
      </section>

      {/* Main two-column */}
      <section className="px-4 lg:px-8 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
        {/* Left: Contact info */}
        <div className="lg:col-span-1">
          <div className="p-4 lg:p-6 rounded-2xl border shadow-sm">
            <h2 className="text-xl font-semibold">Thông tin liên hệ</h2>
            <div className="mt-2 text-sm text-gray-500">
              Mở cửa: 8:30 - 21:00 (T2 - CN)
            </div>

            <div className="mt-4 divide-y">
              <InfoItem
                label="Hotline"
                href="tel:0900000000"
                icon={<IconCall className="w-6 h-6 fill-black" />}
              >
                0935 255 096
              </InfoItem>
              <InfoItem
                label="Facebook Messenger"
                href="https://m.me/lylyiphone"
                icon={<IconMessenger className="w-6 h-6 fill-black" />}
              >
                Chat với Ly iPhone
              </InfoItem>
              <InfoItem
                label="Email"
                href="mailto:ninhbkc@gmail.com"
                icon={<IconEmail className="w-6 h-6 fill-black" />}
              >
                ninhbkc@gmail.com
              </InfoItem>
              <InfoItem
                label="Địa chỉ"
                href="https://maps.app.goo.gl/mw3R4icx7Qa8TaFh8"
                icon={<IconLocationPin className="w-6 h-6 fill-black" />}
              >
                253 Hùng Vương, Tam Kỳ
              </InfoItem>
            </div>

            {/* Quick actions on mobile */}
            <div className="mt-4 flex lg:hidden gap-3">
              <a
                href="tel:0935255096"
                className="flex-1 px-4 py-2 rounded-xl bg-black text-white text-center"
              >
                Gọi ngay
              </a>
              <a
                href="https://m.me/lylyiphone"
                className="flex-1 px-4 py-2 rounded-xl border text-center"
              >
                Messenger
              </a>
            </div>
          </div>

          {/* FAQ / Quick help */}
          <div className="mt-6 p-4 lg:p-6 rounded-2xl border">
            <h3 className="text-lg font-semibold">Hỗ trợ nhanh</h3>
            <ul className="mt-3 list-disc ml-5 space-y-1 text-gray-700">
              <li>Bảo hành - đổi trả</li>
              <li>Đổi cũ lấy mới</li>
              <li>Mua trả góp</li>
              <li>Phụ kiện & lắp đặt</li>
            </ul>
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-2">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="p-4 lg:p-6 rounded-2xl border shadow-sm bg-white"
          >
            <h2 className="text-xl font-semibold">Gửi yêu cầu</h2>
            <p className="text-sm text-gray-500 mt-1">
              Điền thông tin bên dưới - chúng tôi sẽ liên hệ trong giờ làm việc.
            </p>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Họ và tên *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Nguyễn Văn A"
                />
                {errors.name && (
                  <p className="text-xs text-red-600 mt-1">{errors.name}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Số điện thoại *</label>
                <input
                  ref={phoneRef}
                  name="phone"
                  inputMode="tel"
                  value={form.phone}
                  onChange={onChange}
                  required
                  className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="0900 000 000"
                />
                {errors.phone && (
                  <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="you@email.com"
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Chủ đề</label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                >
                  <option>Hỗ trợ tư vấn</option>
                  <option>Báo giá sản phẩm</option>
                  <option>Chính sách bảo hành</option>
                  <option>Khác</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm text-gray-600">Nội dung *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Mô tả nhu cầu của bạn…"
              />
              {errors.message && (
                <p className="text-xs text-red-600 mt-1">{errors.message}</p>
              )}
            </div>

            <div className="mt-5 flex gap-3 justify-end">
              {/* <p className="text-xs text-gray-500">
                Bằng cách gửi, bạn đồng ý với{" "}
                <a href="#" className="underline">
                  Điều khoản bảo mật
                </a>
                .
              </p> */}
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 rounded-xl bg-black text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {submitting ? "Đang gửi…" : "Gửi yêu cầu"}
              </button>
            </div>

            {status.type !== "idle" && (
              <div
                className={`mt-3 text-sm ${
                  status.type === "success" ? "text-green-700" : "text-red-600"
                }`}
                aria-live="polite"
              >
                {status.msg}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Map */}
      <section className="px-4 lg:px-8 pb-10 max-w-6xl mx-auto">
        <h2 className="text-lg lg:text-2xl font-semibold mb-3">Bản đồ</h2>
        <div className="rounded-2xl overflow-hidden border aspect-[16/9]">
          <iframe
            title="Bản đồ Ly iPhone"
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3843.4768338865!2d108.47733807536589!3d15.566151985042815!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3169dddb713eca29%3A0xcdc9cda13c108564!2sLy%20iPhone!5e0!3m2!1svi!2s!4v1734669249328!5m2!1svi!2s"
          />
        </div>
      </section>
    </div>
  );
}
